import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HandCoins, Printer, Search } from "lucide-react";
import usePoll from "@/hooks/usePoll";
import { useAuth } from "@/context/AuthContext";
import { api, dt, errMsg } from "@/lib/api";
import { Empty, ErrorBox, ExportBar, Loading, Money, PageHeader, Sel } from "@/components/common";

function QuickPay() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const find = async (e) => {
    e.preventDefault(); setErr("");
    try { const { data } = await api.get("/sales", { params: { q } }); setRes(data.slice(0, 8)); if (!data.length) setErr("لا توجد عملية بيع مطابقة"); } catch (ex) { setErr(errMsg(ex)); }
  };
  return (
    <div className="k-card p-5 mb-5" data-testid="quick-pay">
      <div className="font-display font-bold text-lg mb-3 flex items-center gap-2"><HandCoins className="w-5 h-5" /> تسجيل دفعة جديدة</div>
      <form onSubmit={find} className="flex gap-2">
        <input className="k-input" placeholder="رقم الكبش، رقم البيع، اسم الزبون أو الهاتف" value={q} onChange={(e) => setQ(e.target.value)} data-testid="quick-pay-search" />
        <button className="k-btn-primary" data-testid="quick-pay-search-btn"><Search className="w-5 h-5" /> بحث</button>
      </form>
      <ErrorBox text={err} />
      {res?.length > 0 && (
        <div className="mt-3 grid sm:grid-cols-2 gap-2">
          {res.map((s) => (
            <button key={s.id} className="text-right rounded-xl border p-3 hover:bg-[#F6F1E7] disabled:opacity-50" disabled={s.remaining <= 0 || s.status !== "active"} onClick={() => nav(`/sales/${s.id}?pay=1`)} data-testid={`quick-pay-option-${s.number}`}>
              <div className="flex justify-between"><b>{s.customer_name}</b><span className="num">كبش {s.ear_tag}</span></div>
              <div className="text-sm text-slate-500">{s.number} – المتبقي <Money v={s.remaining} className="text-rose-700" /></div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Payments() {
  const { can, settings } = useAuth();
  const [f, setF] = useState({ q: "", method: "", date_from: "", date_to: "" });
  const [applied, setApplied] = useState(f);
  const params = Object.fromEntries(Object.entries(applied).filter(([, v]) => v));
  const { data, error, loading } = usePoll("/payments", params, 20000);
  const rows = data || [];
  const total = rows.filter((r) => r.status === "valid").reduce((a, r) => a + r.amount, 0);
  const set = (k) => (v) => { const n = { ...f, [k]: v }; setF(n); setApplied(n); };
  return (
    <div>
      <PageHeader title="المدفوعات" subtitle="سجل جميع الدفعات والأقساط ووصولات الدفع">
        <ExportBar name="payments" rows={rows} cols={[{ key: "number", label: "رقم الوصل" }, { key: "sale_number", label: "رقم البيع" }, { key: "customer_name", label: "الزبون" },
          { key: "ear_tag", label: "رقم الكبش" }, { key: "amount", label: "المبلغ" }, { key: "method", label: "الطريقة" }, { key: "reference", label: "المرجع" },
          { key: "remaining", label: "المتبقي بعدها" }, { key: "clerk_name", label: "الموظف" }, { label: "الحالة", get: (r) => (r.status === "valid" ? "معتمدة" : "ملغاة") }, { label: "التاريخ", get: (r) => dt(r.created_at) }]} />
      </PageHeader>
      {can("record_payment") && <QuickPay />}
      <div className="k-card p-4 mb-4 grid grid-cols-2 md:grid-cols-5 gap-3 no-print">
        <form className="col-span-2" onSubmit={(e) => { e.preventDefault(); setApplied(f); }}>
          <input className="k-input" placeholder="رقم الوصل، البيع، الزبون، الكبش… ثم Enter" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="payments-search-input" />
        </form>
        <Sel value={f.method} onChange={set("method")} testid="payments-filter-method"><option value="">كل الطرق</option>{(settings?.payment_methods || []).map((m) => <option key={m}>{m}</option>)}</Sel>
        <input type="date" className="k-input" value={f.date_from} onChange={(e) => set("date_from")(e.target.value)} data-testid="payments-date-from" />
        <input type="date" className="k-input" value={f.date_to} onChange={(e) => set("date_to")(e.target.value)} data-testid="payments-date-to" />
      </div>
      <div className="k-card p-3 mb-4 inline-block">مجموع الدفعات المعتمدة المعروضة: <Money v={total} className="text-lg text-emerald-700" /></div>
      <ErrorBox text={error} />
      {loading && !data ? <Loading /> : (
        <div className="k-card overflow-x-auto">
          <table className="k-table" data-testid="all-payments-table">
            <thead><tr><th>رقم الوصل</th><th>البيع</th><th>الزبون</th><th>الكبش</th><th>المبلغ</th><th>الطريقة</th><th>المتبقي بعدها</th><th>الموظف</th><th>التاريخ</th><th></th></tr></thead>
            <tbody>{rows.map((p) => (
              <tr key={p.id} className={p.status === "cancelled" ? "opacity-60" : ""}>
                <td className="font-bold num">{p.number}{p.status === "cancelled" && <span className="text-xs text-rose-700 block">ملغاة</span>}</td>
                <td><Link className="underline" to={`/sales/${p.sale_id}`}>{p.sale_number}</Link></td><td>{p.customer_name}</td><td className="num">{p.ear_tag}</td>
                <td><Money v={p.amount} /></td><td>{p.method}</td><td><Money v={p.remaining} /></td><td>{p.clerk_name}</td><td className="text-xs whitespace-nowrap">{dt(p.created_at)}</td>
                <td><Link className="k-btn-outline k-btn-sm" to={`/print/payment/${p.id}`} target="_blank"><Printer className="w-4 h-4" /></Link></td>
              </tr>))}</tbody>
          </table>
          {!rows.length && <Empty text="لا توجد مدفوعات" />}
        </div>
      )}
    </div>
  );
}
